/**
 * Auth Controller
 *
 * Handles user authentication for TDAI multi-user mode.
 * Provides login, register, token storage, and session management.
 *
 * @module
 */

import type { OpenClawApp } from "../app.js";

const AUTH_TOKEN_KEY = "tdai_access_token";
const AUTH_REFRESH_KEY = "tdai_refresh_token";
const AUTH_USER_KEY = "tdai_user";

export interface AuthUser {
  id: string;
  email: string;
  name?: string;
  avatarUrl?: string;
  tenantId: string;
}

export interface AuthState {
  /** Whether user is authenticated */
  authenticated: boolean;
  /** Current user info */
  user: AuthUser | null;
  /** Access token */
  accessToken: string | null;
  /** Auth error message */
  error: string | null;
  /** Loading state */
  loading: boolean;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  name?: string;
}

export interface AuthResponse {
  ok: boolean;
  accessToken?: string;
  refreshToken?: string;
  expiresIn?: number;
  user?: AuthUser;
  error?: string;
  code?: string;
}

/**
 * Get stored access token.
 */
export function getStoredToken(): string | null {
  try {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  } catch {
    return null;
  }
}

/**
 * Get stored refresh token.
 */
export function getStoredRefreshToken(): string | null {
  try {
    return localStorage.getItem(AUTH_REFRESH_KEY);
  } catch {
    return null;
  }
}

/**
 * Get stored user info.
 */
export function getStoredUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem(AUTH_USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Store auth tokens and user info.
 */
export function storeAuth(response: AuthResponse): void {
  try {
    if (response.accessToken) {
      localStorage.setItem(AUTH_TOKEN_KEY, response.accessToken);
    }
    if (response.refreshToken) {
      localStorage.setItem(AUTH_REFRESH_KEY, response.refreshToken);
    }
    if (response.user) {
      localStorage.setItem(AUTH_USER_KEY, JSON.stringify(response.user));
    }
  } catch {
    // localStorage may be unavailable
  }
}

/**
 * Clear stored auth.
 */
export function clearAuth(): void {
  try {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_REFRESH_KEY);
    localStorage.removeItem(AUTH_USER_KEY);
  } catch {
    // localStorage may be unavailable
  }
}

/**
 * Get gateway base URL from settings.
 */
function getGatewayBaseUrl(gatewayUrl: string): string {
  try {
    const url = new URL(gatewayUrl);
    // Convert ws:// to http://, wss:// to https://
    const protocol = url.protocol === "wss:" ? "https:" : "http:";
    return `${protocol}//${url.host}`;
  } catch {
    // Fallback to current origin
    return window.location.origin;
  }
}

/**
 * Login with email and password.
 */
export async function login(app: OpenClawApp, request: LoginRequest): Promise<AuthResponse> {
  const baseUrl = getGatewayBaseUrl(app.settings.gatewayUrl);

  try {
    const response = await fetch(`${baseUrl}/auth/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    });

    const data = (await response.json()) as AuthResponse;

    if (data.ok && data.accessToken) {
      storeAuth(data);
    }

    return data;
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : "Network error",
    };
  }
}

/**
 * Register new account.
 */
export async function register(app: OpenClawApp, request: RegisterRequest): Promise<AuthResponse> {
  const baseUrl = getGatewayBaseUrl(app.settings.gatewayUrl);

  try {
    const response = await fetch(`${baseUrl}/auth/register`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    });

    const data = (await response.json()) as AuthResponse;

    if (data.ok && data.accessToken) {
      storeAuth(data);
    }

    return data;
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : "Network error",
    };
  }
}

/**
 * Refresh access token.
 */
export async function refreshToken(app: OpenClawApp): Promise<AuthResponse> {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) {
    return { ok: false, error: "No refresh token" };
  }

  const baseUrl = getGatewayBaseUrl(app.settings.gatewayUrl);

  try {
    const response = await fetch(`${baseUrl}/auth/refresh`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ refreshToken }),
    });

    const data = (await response.json()) as AuthResponse;

    if (data.ok && data.accessToken) {
      storeAuth(data);
    } else {
      // Refresh failed - clear stored auth
      clearAuth();
    }

    return data;
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : "Network error",
    };
  }
}

/**
 * Logout.
 */
export async function logout(app: OpenClawApp): Promise<void> {
  const refreshToken = getStoredRefreshToken();
  const baseUrl = getGatewayBaseUrl(app.settings.gatewayUrl);

  // Clear local storage first
  clearAuth();

  // Try to invalidate refresh token on server
  if (refreshToken) {
    try {
      await fetch(`${baseUrl}/auth/logout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refreshToken }),
      });
    } catch {
      // Ignore errors - we've already cleared local storage
    }
  }
}

/**
 * Check if user auth mode is enabled (vs token mode).
 * Returns true if we should show login form instead of token prompt.
 */
export function isUserAuthMode(app: OpenClawApp): boolean {
  // If there's already a JWT token stored, we're in user auth mode
  const storedToken = getStoredToken();
  if (storedToken) {
    return true;
  }

  // If there's no token in URL/settings but gateway is configured,
  // and auth failed, we should try user auth mode
  const hasUrlToken = app.settings.token?.trim();
  if (!hasUrlToken && app.lastError?.includes("token")) {
    return true;
  }

  return false;
}

/**
 * Initialize auth state from storage.
 */
export function initAuthState(): AuthState {
  const accessToken = getStoredToken();
  const user = getStoredUser();

  return {
    authenticated: Boolean(accessToken && user),
    user,
    accessToken,
    error: null,
    loading: false,
  };
}
